// sessionChecker.js
import axios from 'axios';
import { clearAllSessionData } from '../../utils/logoutHandler';

const API_URL = process.env.REACT_APP_API_URL;
const broadcastChannel = new BroadcastChannel('session_channel');

/**
 * Roles are stored inconsistently by different backend endpoints ("CARAA",
 * "TPRM User", "TPRM USER", etc). Normalize to upper-case with no whitespace
 * so role comparisons work regardless of which flow (OTP vs OTP-bypass, etc)
 * produced the stored value.
 */
export const normalizeRole = (role) => (role || '').toString().trim().toUpperCase().replace(/\s+/g, '');

/**
 * @param {string|string[]} [expectedRole] - Role(s) that belong to the login
 *   page calling this check. When provided, a session for a role outside
 *   this group is ignored so the check only reacts to sessions that are
 *   part of the same single-active-session policy group (e.g. the 4 core
 *   portals - Cara Admin/User, TPRM Admin/User - share one group so ANY of
 *   them blocks the others, while Superadmin/VRM stay in their own group).
 */
export const checkExistingSession = async (expectedRole) => {
  try {
    const username = localStorage.getItem('username');
    const userId = localStorage.getItem('user_id');
    const role = localStorage.getItem('role');
    const email = localStorage.getItem('email');
    const firstName = localStorage.getItem('first_name');
    const lastName = localStorage.getItem('last_name');
    const deviceType = localStorage.getItem('device_type');
    const browser = localStorage.getItem('browser');

    // If no data in localStorage, no existing session
    if (!username || !userId || !role) {
      return { hasSession: false, message: null };
    }

    // Session belongs to a different single-session-policy group - not relevant here
    if (expectedRole) {
      const allowedRoles = (Array.isArray(expectedRole) ? expectedRole : [expectedRole]).map(normalizeRole);
      if (!allowedRoles.includes(normalizeRole(role))) {
        return { hasSession: false, message: null };
      }
    }

    // localStorage has session data - this means user is logged in on this browser

    return {
      hasSession: true,
      message: `You are already logged in as ${username} (${role}).`,
      userData: { username, role, userId, email, firstName, lastName, deviceType, browser }
    };
  } catch (error) {
    console.error('Error checking existing session:', error);
    // Don't clear localStorage on error - keep the session data
    // User can manually logout if needed
    return { hasSession: false, message: null };
  }
};

export const clearSessionData = () => {
  // Use centralized clear function for consistency
  clearAllSessionData();
  sessionStorage.removeItem('loginType');
  // Broadcast logout to other tabs
  broadcastChannel.postMessage({ type: 'LOGOUT' });
};

export const isCorrectLoginRoute = (currentRoute, userRole) => {
  const routeRoleMap = {
    '/cara_login': 'CARA',
    '/vrm_login': 'VRM',
    '/tprm_login': 'TPRM',
    '/audit_login': 'AUDIT',
    '/login': 'DEFAULT',
    '/superadmin_login': 'SUPERADMIN',
    '/internal_staff_login': 'INTERNALSTAFF',
    '/internal_staff_login': 'INTERNALSTAFF'
  };

  const expectedRole = routeRoleMap[currentRoute];
  return expectedRole === userRole || expectedRole === 'DEFAULT';
};